// Étape 16 — Page « Mes leads » + déclaration rapide
import { useState, useEffect } from "react";
import {
  C, Btn, Input, Textarea, Select, Modal, FormField, Alert, Badge, Spinner, fdate, fmtEur,
  leadsV2Api, leadsExtraApi, errMsg, LEAD_SOURCES, LEAD_BESOINS, leadStatutLabel,
} from "./shared";

export const DeclarerLeadModal = ({ onClose, onSaved, onOpenLead, isAdmin, conseillers = [] }) => {
  const [f, setF] = useState({ prenom: "", nom: "", telephone: "", email: "", besoin: "", source: "", note: "", contact_ok: false, conseillerId: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [existant, setExistant] = useState(null);
  const set = (k, v) => setF({ ...f, [k]: v });
  const ok = f.prenom.trim() && f.nom.trim() && (f.telephone.trim() || f.email.trim()) && f.besoin && f.source && f.contact_ok && (!isAdmin || f.conseillerId);
  const submit = async () => {
    setSaving(true); setErr(""); setExistant(null);
    try {
      await leadsV2Api.create({ ...f, conseillerId: f.conseillerId || undefined });
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "doublon_self") setExistant(d.lead_id);
      setErr(errMsg(e));
    } finally { setSaving(false); }
  };
  return (
    <Modal title="Déclarer un lead" onClose={onClose}
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={saving || !ok} onClick={submit} data-testid="lead-declare-submit">{saving ? "…" : "Déclarer le lead"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span data-testid="lead-declare-error">{err}{existant && <> <Btn small onClick={() => { onClose(); onOpenLead(existant); }} data-testid="lead-open-existing">Ouvrir le lead</Btn></>}</span></Alert>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FormField label="Prénom" required><Input value={f.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="lead-prenom" /></FormField>
        <FormField label="Nom" required><Input value={f.nom} onChange={(e) => set("nom", e.target.value)} data-testid="lead-nom" /></FormField>
        <FormField label="Téléphone"><Input type="tel" inputMode="tel" value={f.telephone} onChange={(e) => set("telephone", e.target.value)} placeholder="0690 12 34 56" data-testid="lead-tel" /></FormField>
        <FormField label="E-mail"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" /></FormField>
      </div>
      <div style={{ fontSize: 11, color: C.muted, margin: "4px 0 12px" }}>Téléphone et/ou e-mail : au moins un des deux.</div>
      <FormField label="Type de besoin" required>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {LEAD_BESOINS.map(b => (
            <button key={b} type="button" onClick={() => set("besoin", b)} data-testid={`lead-besoin-${b}`}
              style={{ padding: "10px", borderRadius: 8, border: f.besoin === b ? `2px solid ${C.gold}` : `1px solid ${C.border}`, background: f.besoin === b ? C.goldlt : "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit", color: C.navy }}>{b}</button>
          ))}
        </div>
      </FormField>
      <div style={{ height: 12 }} />
      <FormField label="Source" required>
        <Select value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="lead-source">
          <option value="">— Choisir —</option>
          {LEAD_SOURCES.map(s => <option key={s}>{s}</option>)}
        </Select>
      </FormField>
      <div style={{ height: 12 }} />
      {isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <FormField label="Consultant" required>
            <Select value={f.conseillerId} onChange={(e) => set("conseillerId", e.target.value)} data-testid="lead-consultant">
              <option value="">— Choisir —</option>
              {conseillers.map(c => <option key={c.id} value={c.id}>{c.nom} ({c.id})</option>)}
            </Select>
          </FormField>
        </div>
      )}
      <FormField label="Note (facultatif)"><Textarea value={f.note} onChange={(e) => set("note", e.target.value)} style={{ minHeight: 56 }} data-testid="lead-note" /></FormField>
      <label style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-start", fontSize: 12, color: C.text, cursor: "pointer" }}>
        <input type="checkbox" checked={f.contact_ok} onChange={(e) => set("contact_ok", e.target.checked)} style={{ width: 18, height: 18, accentColor: C.gold }} data-testid="lead-contact-ok" />
        <span><strong>Le prospect a accepté d'être contacté par AUPRÉA</strong> (obligatoire)</span>
      </label>
    </Modal>
  );
};

const FILTRES = [
  ["tous", "Tous", () => true],
  ["traiter", "À traiter", (l) => l.a_relancer || l.statut === "nouveau"],
  ["nouveau", "Nouveau", (l) => l.statut === "nouveau"],
  ["contacte", "Contacté", (l) => l.statut === "contacte"],
  ["rdv", "RDV fixé", (l) => l.statut === "rdv_fixe"],
  ["cours", "En cours", (l) => ["bilan_realise", "proposition_envoyee", "signee"].includes(l.statut)],
  ["clients", "Clients", (l) => l.statut === "client"],
  ["perdus", "Perdus", (l) => l.statut === "perdu"],
];

const th = { padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", borderBottom: `.5px solid ${C.border}`, background: C.gray2, whiteSpace: "nowrap" };
const td = { padding: "11px 14px", fontSize: 12 };

const AdminLeadAlerts = ({ onOpen, reload }) => {
  const [dbl, setDbl] = useState([]);
  const [dorm, setDorm] = useState([]);
  const load = () => { leadsV2Api.doublons().then(setDbl).catch(() => {}); leadsV2Api.dormants().then(setDorm).catch(() => {}); };
  useEffect(load, []);
  const trancher = async (id, d) => { await leadsV2Api.trancher(id, d); load(); reload(); };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
      <div data-testid="admin-doublons" style={{ background: "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${C.red}`, borderRadius: 12, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.red, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Doublons à trancher ({dbl.length})</div>
        {!dbl.length && <div style={{ fontSize: 12, color: C.muted }}>Aucune alerte.</div>}
        {dbl.map(a => (
          <div key={a.id} data-testid={`doublon-${a.id}`} style={{ fontSize: 12, padding: "8px 0", borderTop: `.5px solid ${C.border}` }}>
            <div><strong>{a.existant?.prospect}</strong> — suivi par {a.conseiller_existant} (déclaré le {fdate(a.existant?.date)})</div>
            <div style={{ color: C.mid }}>Nouvelle déclaration par {a.tentative?.conseillerId} le {fdate(a.at)} · {a.tentative?.source}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <Btn small onClick={() => onOpen(a.lead_existant)}>Voir</Btn>
              <Btn small variant="gold" onClick={() => trancher(a.id, "garder")} data-testid={`doublon-garder-${a.id}`}>Laisser au 1er</Btn>
              <Btn small variant="navy" onClick={() => trancher(a.id, "attribuer_second")} data-testid={`doublon-second-${a.id}`}>Attribuer au 2nd</Btn>
            </div>
          </div>
        ))}
      </div>
      <div data-testid="admin-dormants" style={{ background: "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: 14, maxHeight: 260, overflowY: "auto" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Leads dormants — 30 j sans action ({dorm.length})</div>
        {!dorm.length && <div style={{ fontSize: 12, color: C.muted }}>Aucun lead dormant.</div>}
        {dorm.map(l => (
          <div key={l.id} onClick={() => onOpen(l.id)} style={{ fontSize: 12, padding: "6px 0", borderTop: `.5px solid ${C.border}`, cursor: "pointer" }}>
            <strong>{l.prospect}</strong> · {l.conseillerId} · {l.statut_label} · {l.jours_inactif} j
          </div>
        ))}
      </div>
    </div>
  );
};

export const LeadsPage = ({ conseillers, onAddLead, onOpen, isAdmin, refreshKey }) => {
  const [leads, setLeads] = useState(null);
  const [filtre, setFiltre] = useState("tous");
  const [q, setQ] = useState("");
  const load = () => leadsV2Api.list().then(setLeads).catch(() => setLeads([]));
  useEffect(() => { load(); }, [refreshKey]);
  if (!leads) return <Spinner />;
  const fn = FILTRES.find(x => x[0] === filtre)[2];
  const s = q.trim().toLowerCase();
  const list = leads.filter(fn).filter(l => !s || `${l.prospect} ${l.identite?.telephone || ""} ${l.identite?.email || ""}`.toLowerCase().includes(s));
  const nbRelance = leads.filter(l => l.a_relancer).length;
  const exportCsv = () => fetch(leadsExtraApi.exportUrl(), { headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } })
    .then(r => r.blob()).then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "leads_auprea.csv"; a.click(); });
  return (
    <div data-testid="leads-page">
      {isAdmin && <AdminLeadAlerts onOpen={onOpen} reload={load} />}
      {nbRelance > 0 && <Alert type="warn"><span>⏰</span><span data-testid="leads-a-relancer-count"><strong>{nbRelance} lead(s) à relancer</strong> — sans action depuis 7 jours ou plus.</span></Alert>}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {FILTRES.map(([k, label, f]) => (
            <button key={k} onClick={() => setFiltre(k)} data-testid={`leads-filtre-${k}`}
              style={{ padding: "5px 12px", borderRadius: 20, border: `.5px solid ${C.border}`, background: filtre === k ? C.navy : "#fff", color: filtre === k ? "#fff" : C.mid, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {label} <span style={{ opacity: .6 }}>{leads.filter(f).length}</span>
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" style={{ width: 180 }} data-testid="leads-search" />
          {isAdmin && <Btn small onClick={exportCsv} data-testid="leads-export">⬇ Export CSV</Btn>}
          <Btn variant="gold" onClick={onAddLead} data-testid="leads-declarer">+ Déclarer un lead</Btn>
        </div>
      </div>
      <div style={{ background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}`, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Prospect", "Besoin", "Source", "Statut", "Commission", ...(isAdmin ? ["Consultant"] : []), "Déclaré le", "Dernière action"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {list.map(l => {
              const cons = conseillers.find(c => c.id === l.conseillerId);
              return (
                <tr key={l.id} onClick={() => onOpen(l.id)} data-testid={`lead-row-${l.id}`} style={{ borderBottom: `.5px solid ${C.border}`, cursor: "pointer", background: l.a_relancer ? "#FFF8F6" : "#fff" }}>
                  <td style={{ ...td, fontWeight: 600, color: C.navy, fontSize: 13 }}>{l.prospect || "—"}
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{[l.identite?.telephone, l.identite?.email].filter(Boolean).join(" · ")}</div></td>
                  <td style={td}>{l.besoin || "—"}</td>
                  <td style={{ ...td, color: C.mid }}>{l.source || "—"}</td>
                  <td style={td}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}><Badge text={leadStatutLabel(l)} />{l.a_relancer && <span data-testid={`lead-relance-${l.id}`}><Badge text="À relancer" /></span>}</div></td>
                  <td style={td} data-testid={`lead-commission-${l.id}`}>{l.commission ? <><strong>{fmtEur(l.commission.montant)}</strong><div style={{ fontSize: 10, color: C.muted }}>{l.commission.statut_label}</div></> : <span style={{ color: C.muted }}>—</span>}</td>
                  {isAdmin && <td style={td}>{cons?.nom || l.conseillerId}</td>}
                  <td style={{ ...td, color: C.muted }}>{fdate(l.date)}</td>
                  <td style={{ ...td, color: C.muted }}>{fdate(l.last_action_at)} <span style={{ fontSize: 10 }}>({l.jours_inactif} j)</span></td>
                </tr>
              );
            })}
            {!list.length && <tr><td colSpan="8" style={{ textAlign: "center", padding: 40, color: C.muted }} data-testid="leads-vide">Aucun lead</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
